import { FunctionComponent, ReactNode } from 'react'
import styled, { css } from 'styled-components'

interface BlinkerProps {
  children: ReactNode
  labelColor?: string
}

const StyledBlinker = styled.div<Pick<BlinkerProps, 'labelColor'>>`
  ${({
    theme: {
      colors: { staticWhite, fillBrand },
      typography: {
        catchword: {
          small2: { bundledCSS: small2CatchwordCSS },
        },
      },
      utils: { ellipsis },
      spacing: { spacing4 },
    },
    labelColor,
  }) => css`
    display: inline-block;
    box-sizing: border-box;
    padding: 0.28em 0.4em 0.2em;
    margin-bottom: ${spacing4};
    ${ellipsis('100%', 1)};
    ${small2CatchwordCSS};
    text-transform: uppercase;
    color: ${staticWhite};
    background: ${labelColor ?? fillBrand};
    background-size: 400% 100%;
    background-image: linear-gradient(
      to left,
      ${labelColor || fillBrand} 25%,
      ${staticWhite}66 25%,
      ${staticWhite}66 50%,
      ${staticWhite}1a 75%,
      ${staticWhite}00 100%
    );
    animation: background-blinker 1.2s infinite;
    @keyframes background-blinker {
      0% {
        background-position: 100% 50%;
        animation-timing-function: cubic-bezier(0.42, 0, 1, 1);
      }
      25% {
        background-position: 75% 50%;
      }
      40% {
        background-position: 50% 50%;
        animation-timing-function: cubic-bezier(0, 0, 0.58, 1);
      }
      100% {
        background-position: 0 50%;
      }
    }
    &:empty {
      background-color: transparent;
      padding-left: 0;
      padding-right: 0;
      &:before {
        content: '​';
        width: 0;
      }
    }
  `}
`

const StyledBlinkingDot = styled.div`
  ${({
    theme: {
      colors: { staticWhite },
    },
  }) => css`
    display: inline-flex;
    margin-right: 6px;
    &:before {
      content: '';
      box-sizing: border-box;
      width: 8px;
      height: 8px;
      background-color: ${staticWhite};
      border-radius: 50%;
      animation: blinker 1.2s infinite;
    }
    @keyframes blinker {
      0% {
        box-shadow: 0 0 0 0 ${staticWhite};
        animation-timing-function: cubic-bezier(0.42, 0, 1, 1);
      }
      25% {
        box-shadow: 0 0 0 5px ${staticWhite}BF;
      }
      40% {
        box-shadow: 0 0 0 5px ${staticWhite}1A;
        animation-timing-function: cubic-bezier(0, 0, 0.58, 1);
      }
    }
  `}
`

const Blinker: FunctionComponent<BlinkerProps> = ({ children, labelColor }) => (
  <StyledBlinker labelColor={labelColor}>
    <StyledBlinkingDot />
    {children}
  </StyledBlinker>
)

export default Blinker
